import Navbar from '../../components/feature/Navbar';
import Footer from '../home/components/Footer';
import LineFloatingButton from '../../components/feature/LineFloatingButton';
import { Link, useNavigate } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

export default function CargoPage() {
  const navigate = useNavigate();

  useSEO({
    title: '長崎貨物配送サービス | みなとサービス - 即日配送・定期配送対応',
    description: '長崎県の貨物配送サービス。県内全域対応、即日配送可能。企業様向け定期配送、個人様の小口配送まで対応。貨物保険完備で安心。基本料金5,000円〜。お見積もり無料。',
    keywords: '長崎,貨物配送,即日配送,定期配送,小口配送,企業配送,ルート配送,軽貨物,長崎県,配送業者',
    canonical: '/cargo',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: '貨物配送サービス',
      description: '長崎県の貨物配送サービス。県内全域対応、即日配送可能。企業様向け定期配送から個人様の小口配送まで対応します。',
      provider: {
        '@type': 'LocalBusiness',
        name: 'みなとサービス',
        telephone: '0120-743-643',
        address: {
          '@type': 'PostalAddress',
          addressRegion: '長崎県',
          addressCountry: 'JP'
        }
      },
      areaServed: {
        '@type': 'Place',
        name: '長崎県'
      },
      offers: {
        '@type': 'Offer',
        priceSpecification: {
          '@type': 'PriceSpecification',
          price: '5000',
          priceCurrency: 'JPY',
          description: '基本料金（20kmまで）'
        }
      }
    }
  });

  const services = [
    {
      icon: 'ri-time-line',
      title: '即日配送',
      description: '長崎県内なら即日配送可能。お急ぎの荷物も安心してお任せください。'
    },
    {
      icon: 'ri-calendar-check-line',
      title: '定期配送',
      description: '企業様向けの定期配送サービス。ルート配送で効率的な物流をサポート。'
    },
    {
      icon: 'ri-box-3-line',
      title: '小口配送',
      description: '個人のお客様の小さな荷物から、企業様の商品配送まで対応。'
    },
    {
      icon: 'ri-flashlight-line',
      title: '緊急配送',
      description: '急ぎの配送にも対応。緊急時の荷物も迅速にお届けします。'
    }
  ];

  const features = [
    {
      title: '県内全域及び全国対応',
      description: '長崎県内はもちろん、全国各地への配送に対応しています。',
      image: 'https://static.readdy.ai/image/db812a398b3a5f225f48907587ff778a/16d71c9d63a30864268ee8bc3d7ebbb6.jpeg'
    },
    {
      title: '柔軟な配送時間',
      description: '早朝・夜間配送も可能。お客様のご都合に合わせて対応します。',
      image: 'https://public.readdy.ai/ai/img_res/7e9bf53b1876efa8897f68dc9428e0e7.jpg'
    },
    {
      title: '企業様向けサービス',
      description: '定期配送、ルート配送など、企業様のニーズに合わせたプランをご提案。',
      image: 'https://readdy.ai/api/search-image?query=warm%20illustration%20of%20business%20office%20setting%20with%20organized%20packages%20and%20delivery%20schedule%20board%2C%20corporate%20logistics%20concept%20with%20charts%20and%20planning%20elements%2C%20soft%20beige%20cream%20and%20yellow%20tones%2C%20minimalist%20flat%20design%20illustration%2C%20clean%20professional%20workspace%20showing%20business%20delivery%20service%20concept%2C%20gentle%20shadows%20and%20simple%20geometric%20shapes%2C%20welcoming%20atmosphere%20without%20vehicles%20or%20people&width=600&height=400&seq=cargo-feature-illustration-403&orientation=landscape'
    }
  ];

  const pricingRates = [
    {
      range: '基本料金(20kmまで)',
      rate: '¥5,000〜',
      description: '20kmまでの配送基本料金'
    },
    {
      range: '21〜50km',
      rate: '¥220/km',
      description: '1kmあたりの追加料金'
    },
    {
      range: '51〜100km',
      rate: '¥180/km',
      description: '1kmあたりの追加料金'
    },
    {
      range: '101〜150km',
      rate: '¥150/km',
      description: '1kmあたりの追加料金'
    },
    {
      range: '150km以降',
      rate: '¥130/km',
      description: '1kmあたりの追加料金'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[400px] sm:h-[500px] md:h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20corporate%20logistics%20warehouse%20interior%20with%20organized%20cargo%20pallets%20and%20industrial%20shelving%20systems%2C%20clean%20modern%20distribution%20center%20with%20stacked%20packages%20ready%20for%20business%20delivery%2C%20soft%20beige%20cream%20and%20yellow%20ambient%20lighting%2C%20minimalist%20flat%20design%20illustration%20style%2C%20organized%20commercial%20freight%20storage%20facility%20showing%20professional%20logistics%20operations%2C%20simple%20geometric%20shapes%20and%20gentle%20shadows%2C%20efficient%20business%20atmosphere%20without%20text%20labels%20vehicles%20or%20people&width=1200&height=600&seq=cargo-hero-corporate-501&orientation=landscape"
            alt="貨物配送サービス"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-7xl font-bold mb-3 md:mb-6">
            貨物配送サービス
          </h1>
          <p className="text-sm sm:text-base md:text-xl lg:text-2xl mb-3 md:mb-4 text-white/90 leading-relaxed">
            長崎県内全域対応。<br className="sm:hidden" />
            企業様から個人様まで、<br className="hidden sm:block" />
            確実にお届けします
          </p>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4 mb-5 md:mb-8 inline-block border border-white/30">
            <p className="text-xs sm:text-sm md:text-base lg:text-lg font-bold text-white flex items-center gap-2">
              <i className="ri-map-pin-fill text-[#ebe960] text-lg md:text-xl"></i>
              長崎県内・全国各地への<br className="sm:hidden" />
              貨物配送に対応
            </p>
          </div>
          <div className="flex flex-wrap gap-3 md:gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-[#ebe960] text-gray-900 px-6 md:px-8 py-3 md:py-4 rounded-full font-bold text-sm md:text-lg hover:bg-[#d4d34d] transition-all hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
            >
              <i className="ri-mail-line text-lg md:text-xl"></i>
              お問い合わせはこちら
            </Link>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4">
              配送サービスの特徴
            </h2>
            <p className="text-sm md:text-base lg:text-lg text-gray-600">
              お客様のニーズに合わせた柔軟な配送サービス
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-[#f8f6f1] rounded-xl md:rounded-2xl p-4 md:p-8 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-[#ebe960] rounded-xl md:rounded-2xl flex items-center justify-center mb-3 md:mb-6">
                  <i className={`${service.icon} text-xl sm:text-2xl md:text-3xl text-gray-900`}></i>
                </div>
                <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-900 mb-1 md:mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 text-[10px] sm:text-xs md:text-sm lg:text-base leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-8 md:mt-12 bg-[#ebe960] rounded-2xl md:rounded-3xl p-5 md:p-8 text-center">
            <div className="flex items-center justify-center gap-2 md:gap-3 mb-2 md:mb-3">
              <i className="ri-shield-check-fill text-2xl md:text-4xl text-gray-900"></i>
              <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900">
                貨物保険完備
              </h3>
            </div>
            <p className="text-gray-800 text-xs sm:text-sm md:text-base lg:text-lg leading-relaxed">
              お客様の大切な荷物を安心してお預けいただけるよう、<br className="hidden sm:block" />
              万が一の事故に備えて貨物保険に加入しています。
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 md:py-24 bg-[#f8f6f1]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="space-y-10 md:space-y-24">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`flex flex-col ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } gap-6 md:gap-12 items-center`}
              >
                <div className="flex-1 w-full">
                  <div className="w-full aspect-[3/2] md:aspect-[4/3] lg:h-[400px] lg:aspect-auto rounded-xl md:rounded-3xl overflow-hidden">
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-6">
                    {feature.title}
                  </h3>
                  <p className="text-sm md:text-base lg:text-lg text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4">
              料金体系
            </h2>
            <p className="text-sm md:text-base lg:text-lg text-gray-600">
              距離に応じた明確な料金設定
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-[#f8f6f1] rounded-2xl md:rounded-3xl p-5 sm:p-6 md:p-8 lg:p-12">
              <div className="space-y-3 md:space-y-6">
                {pricingRates.map((rate, index) => (
                  <div
                    key={index}
                    className={`flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 md:p-6 rounded-xl md:rounded-2xl ${
                      index === 0 ? 'border-2 border-[#ebe960] bg-white' : ''
                    }`}
                  >
                    <div className="mb-2 sm:mb-0">
                      <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-900 mb-0.5 md:mb-1">
                        {rate.range}
                      </h3>
                      <p className="text-[10px] sm:text-xs md:text-sm lg:text-base text-gray-600">
                        {rate.description}
                      </p>
                    </div>
                    <div className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                      {rate.rate}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 md:mt-8 p-4 md:p-6 bg-white rounded-xl md:rounded-2xl">
                <p className="text-xs sm:text-sm md:text-base font-semibold text-gray-900 mb-2 md:mb-3">
                  ※上記料金は目安です
                </p>
                <div className="text-[10px] sm:text-xs md:text-sm lg:text-base text-gray-600 text-left leading-loose">
                  <p className="mt-2">
                    梱包の有無や積み込み及び荷下ろし作業の条件、<br className="hidden sm:block" />
                    時期 (3・4月)により料金が変動する場合があります。
                  </p>
                  <p className="mt-2">
                    集荷先が遠い場合は、<br className="sm:hidden" />
                    回送料が発生することがございます。
                  </p>
                  <p className="mt-2">
                    有料道路などの交通機関を利用する場合は、<br className="hidden sm:block" />
                    実費を別途ご請求させていただきます。
                  </p>
                  <p className="mt-4">
                    詳しくはお問い合わせください。
                  </p>
                </div>
              </div>

              <div className="mt-4 md:mt-6 p-4 md:p-6 bg-[#ebe960]/20 rounded-xl md:rounded-2xl">
                <h4 className="font-bold text-gray-900 mb-2 md:mb-3 flex items-center gap-2 text-sm md:text-base lg:text-lg">
                  <i className="ri-information-fill text-lg md:text-xl"></i>
                  料金計算例
                </h4>
                <div className="space-y-1.5 md:space-y-2 text-[10px] sm:text-xs md:text-sm lg:text-base text-gray-700 leading-relaxed">
                  <p><strong>30kmの配送:</strong> <br className="sm:hidden" />基本料金 ¥5,000 + (30km - 20km) × ¥220 = ¥7,200</p>
                  <p><strong>80kmの配送:</strong> <br className="sm:hidden" />基本料金 ¥5,000 + (30km × ¥220) + (30km × ¥180) = ¥17,000</p>
                  <p><strong>120kmの配送:</strong> <br className="sm:hidden" />基本料金 ¥5,000 + (30km × ¥220) + (50km × ¥180) + (20km × ¥150) = ¥20,600</p>
                </div>
              </div>

              <div className="mt-4 md:mt-6 p-4 md:p-6 bg-white rounded-xl md:rounded-2xl border-2 border-[#ebe960]">
                <h4 className="font-bold text-gray-900 mb-2 md:mb-3 flex items-center gap-2 text-sm md:text-base lg:text-lg">
                  <i className="ri-calendar-check-line text-lg md:text-xl"></i>
                  定期配送について
                </h4>
                <p className="text-[10px] sm:text-xs md:text-sm lg:text-base text-gray-700 leading-relaxed">
                  企業様向けの定期配送サービスは、<br className="hidden sm:block" />
                  配送頻度やルート、荷物の内容により<br className="sm:hidden" />
                  料金が異なります。<br />
                  お客様のニーズに合わせた<br className="hidden sm:block" />
                  最適なプランをご提案いたしますので、<br />
                  お気軽にお見積もりをご依頼ください。
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 md:mt-12 text-center">
            <a
              href="https://lin.ee/Rm6h2gK"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#06C755] text-white px-6 md:px-8 py-3 md:py-4 rounded-full font-bold text-sm md:text-lg hover:bg-[#05b34c] transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-line-fill text-lg md:text-xl"></i>
              LINEで見積もり依頼
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-24 bg-[#f8f6f1]">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-6">
            配送のご依頼・お見積もり
          </h2>
          <p className="text-sm md:text-base lg:text-lg text-gray-600 mb-5 md:mb-8">
            お気軽にお問い合わせください
          </p>
          <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 mb-6 md:mb-8 inline-block">
            <p className="text-sm md:text-base lg:text-lg font-bold text-gray-900 flex items-center gap-2 md:gap-3">
              <i className="ri-map-2-fill text-[#ebe960] text-xl md:text-2xl"></i>
              長崎県内・全国各地への<br className="sm:hidden" />
              貨物配送に対応
            </p>
            <p className="text-xs md:text-sm lg:text-base text-gray-600 mt-2">
              遠方への配送もお任せください。<br className="sm:hidden" />
              詳しくはお問い合わせください。
            </p>
          </div>

          <div className="flex justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 md:gap-3 bg-gray-900 text-white px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 rounded-full text-base sm:text-lg md:text-xl font-bold hover:bg-gray-800 transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-fill text-lg sm:text-xl md:text-2xl"></i>
              お問い合わせはこちら
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <LineFloatingButton />
    </div>
  );
}
